import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";

interface Props {
  name: string;
  editing: boolean;
  onRename: (name: string) => void;
  onDone: () => void;
}

type FieldProps = Omit<Props, "editing">;

// contractsapi refuses a longer name.
const PROPOSAL_NAME_MAX_LENGTH = 256;

// Mounted per Rename, so each one starts from the current name. Enter or leaving
// the field confirms; Escape, or a name left blank, keeps the old one.
const TitleField: React.FC<FieldProps> = ({ name, onRename, onDone }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(name);
  // Enter and Escape end the edit before the field's blur can.
  const settled = useRef(false);

  const finish = (confirmed: boolean) => {
    if (settled.current) return;
    settled.current = true;
    const next = draft.trim();
    if (confirmed && next && next !== name) onRename(next);
    onDone();
  };

  return (
    <Input
      autoFocus
      value={draft}
      maxLength={PROPOSAL_NAME_MAX_LENGTH}
      onChange={(event) => setDraft(event.target.value)}
      onFocus={(event) => event.target.select()}
      onBlur={() => finish(true)}
      onKeyDown={(event) => {
        // An Enter that picks an IME candidate is part of typing the name.
        if (event.nativeEvent.isComposing) return;
        if (event.key === "Enter" || event.key === "Escape") {
          event.preventDefault();
          finish(event.key === "Enter");
        }
      }}
      aria-label={t("contracts.proposals.editor.renameLabel")}
      containerClassName="min-w-0 w-[360px] ml-1"
      className="h-8 text-xl font-semibold text-h1"
      data-test="proposal-title-input"
    />
  );
};

// The breadcrumb's last crumb, which Rename turns into a field.
export const ProposalTitle: React.FC<Props> = ({ editing, ...field }) =>
  editing ? (
    <TitleField {...field} />
  ) : (
    <Text
      variant="lgSubtitleH1"
      // The rep types the name, so it shows as typed, not capitalized.
      className="px-2 truncate min-w-0 normal-case"
      data-test="proposal-title"
    >
      {field.name}
    </Text>
  );
